/**
 * Logger Utility
 *
 * Centralized logging utility that:
 * - Provides structured logging with context
 * - Can be easily replaced with a logging service (Sentry, etc.)
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  readonly [key: string]: unknown;
}

/**
 * Format log message with context
 */
function formatMessage(message: string, context?: LogContext): string {
  if (!context || Object.keys(context).length === 0) {
    return message;
  }

  const contextStr = Object.entries(context)
    .map(([key, value]) => `${key}=${JSON.stringify(value)}`)
    .join(' ');

  return `${message} ${contextStr}`;
}

/**
 * Logger interface
 */
export const logger = {
  /**
   * Debug logs - detailed information for debugging
   */
  debug: (message: string, ...args: unknown[]): void => {
    console.warn(`[DEBUG] ${message}`, ...args);
  },

  /**
   * Info logs - general information
   */
  info: (message: string, ...args: unknown[]): void => {
    console.warn(`[INFO] ${message}`, ...args);
  },

  /**
   * Warning logs - warnings that don't break functionality
   */
  warn: (message: string, ...args: unknown[]): void => {
    console.warn(`[WARN] ${message}`, ...args);
  },

  /**
   * Error logs - errors that should be investigated
   * Always logged (even in production) - can be sent to error tracking service
   */
  error: (message: string, ...args: unknown[]): void => {
    // Always log errors, even in production
    console.error(`[ERROR] ${message}`, ...args);

    // TODO: Send to error tracking service (Sentry, etc.) in production
    // if (!import.meta.env.DEV) {
    //   errorTrackingService.captureException(new Error(message), { extra: args });
    // }
  },

  /**
   * Log with context (structured logging)
   */
  logWithContext: (level: LogLevel, message: string, context?: LogContext): void => {
    const formattedMessage = formatMessage(message, context);

    switch (level) {
      case 'debug':
        logger.debug(formattedMessage);
        break;
      case 'info':
        logger.info(formattedMessage);
        break;
      case 'warn':
        logger.warn(formattedMessage);
        break;
      case 'error':
        logger.error(formattedMessage);
        break;
    }
  },
};
